import { AidaService } from '@adaptiva/ai/common';
import { BaseHeader, IProductNavigation } from '@adaptiva/core/app-shell';
import { EnvService, filterNullish } from '@adaptiva/core/common';
import { ErrorHandlerService } from '@adaptiva/core/error-handler';
import { Licensing } from '@adaptiva/core/licensing';
import { LoggingService } from '@adaptiva/core/logging';
import { RoutingService } from '@adaptiva/core/routing';
import { UiService } from '@adaptiva/core/ui';
import { AuthenticationService, LicenseManagerService, ObjectSecurityManagerService } from '@adaptiva/data-service';
import { Component, inject, OnInit, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { BehaviorSubject, distinctUntilChanged, switchMap, takeUntil } from 'rxjs';
import { AdminPortalHeaderMenuController } from './admin-portal-header-menu-controller';

@Component({
	standalone: false,
	selector: 'ada-admin-portal-header',
	templateUrl: './admin-portal-header.component.html',
	styleUrl: './admin-portal-header.component.scss',
	providers: [AdminPortalHeaderMenuController]
})
export class AdminPortalHeaderComponent extends BaseHeader implements OnInit {

	public activeProduct$: BehaviorSubject<string> = new BehaviorSubject('');

	public readonly menuController: AdminPortalHeaderMenuController = inject(AdminPortalHeaderMenuController);

	public readonly envService: EnvService = inject(EnvService);

	public readonly auth: AuthenticationService = inject(AuthenticationService);

	public readonly logger: LoggingService = inject(LoggingService);

	public readonly licenseManager: LicenseManagerService = inject(LicenseManagerService);

	protected readonly routing: RoutingService = inject(RoutingService);

	protected readonly errorHandler: ErrorHandlerService = inject(ErrorHandlerService);

	protected readonly ui: UiService = inject(UiService);

	protected readonly aida: AidaService = inject(AidaService);

	protected readonly securityManager: ObjectSecurityManagerService = inject(ObjectSecurityManagerService);

	protected readonly isSuperAdmin: Signal<boolean> = toSignal(
		this.auth.user.pipe(
			filterNullish(),
			switchMap(() => this.securityManager.adminIsSuper(true))
		),
		{
			initialValue: false,
		}
	);

	public ngOnInit(): void {
		this._consumeLicenses();
		this._consumeActiveProduct();
	}

	public logout(): void {
		this.auth.logout().pipe(
			this.takeOneUntilDestroyed()
		).subscribe({
			next: () => {
				this.routing.navigateByUrl('/login');
			},
			error: (e: Error) => {
				this.errorHandler.defaultErrorHandler(e, 'AdminPortalHeaderComponent.logout');
			}
		});
	}

	private _consumeLicenses(): void {
		this.licenseManager.licenseStates.pipe(
			filterNullish(),
			this.takeUntilDestroyed()
		).subscribe({
			next: (_states: Map<string, Licensing.States>) => {
				this._generateHeaderLinks(_states);
			}
		});
	}

	private _consumeActiveProduct(): void {
		this.licenseManager.currentProduct.pipe(
			distinctUntilChanged(),
			takeUntil(this.destroyed$)
		).subscribe({
			next: (_currentProduct: string) => {
				this.activeProduct$.next(_currentProduct || '');
			}
		});
	}

	private _generateHeaderLinks(_productStates: Map<string, Licensing.States>): void {
		const _productNav: IProductNavigation[] = this._productNavigation.getAllProductNavigations()
			.filter((_nav: IProductNavigation) => {
				if (_nav.productGroup) {
					_nav.children = _nav.children?.filter((_child: IProductNavigation) => {
						return this._filterNavigation(_child, _productStates);
					}) || [];

					return _nav.children?.length > 0 || false;
				}

				return this._filterNavigation(_nav, _productStates);
			})
			.sort((a: IProductNavigation, b: IProductNavigation) => {
				return this.licenseManager.getUserFriendlyProductName(a.productName).localeCompare(
					this.licenseManager.getUserFriendlyProductName(b.productName)
				);
			});

		this.productNavigation$.next(_productNav);
	}

	private _filterNavigation(_nav: IProductNavigation, _productStates: Map<string, Licensing.States>): boolean {
		const _productName: string = this.licenseManager.getProductName(_nav.productName);
		const _productState: number = _productStates.get(_productName);

		if (!_productState || _nav.children?.length) {
			if (_nav.children?.length) {
				_nav.children = _nav.children.filter((_child: IProductNavigation) => {
					const _childProductName: string = this.licenseManager.getProductName(_child.productName);
					const _childState: Licensing.States = _productStates.get(_childProductName);

					return !!_childState;
				}).map((_child: IProductNavigation) => {
					// set state to value from local map
					_child.state = _productStates.get(this.licenseManager.getProductName(_child.productName));

					return _child;
				});
			}

			return _nav.children?.length > 0;
		}

		// product has license state, set on product navigation item
		_nav.state = _productState;

		return true;
	}

}
